import { SUPPORTED_LOCALES, type SupportedLocaleCode } from './index';

/**
 * Locale list metadata for the language picker.
 *
 * Built to scale past the current four languages: adding one means adding its
 * code + native label to SUPPORTED_LOCALES and its translation JSON. Everything
 * the picker needs beyond that — the English name and the searchable text — is
 * derived here from `Intl.DisplayNames`, so a new locale needs no picker
 * changes, and the picker grows a search box on its own once the list is long
 * enough to need one.
 */

export interface LocaleOption {
  code: SupportedLocaleCode;
  /** The language in its own language, e.g. "Español" — the primary label. */
  native: string;
  /**
   * English name, e.g. "Mexican Spanish". Shown as a subtitle and included in
   * the search text so an English speaker can find 한국어 by typing "korean",
   * and so es-MX / es-ES stay distinguishable when both exist.
   */
  english: string;
  /** Lower-cased haystack: native + English + code. */
  searchText: string;
}

/**
 * Above this many languages the picker shows a search box. Four doesn't need
 * one; twenty does.
 */
export const LOCALE_SEARCH_THRESHOLD = 6;

function displayNameIn(locale: string, code: string): string | null {
  try {
    const name = new Intl.DisplayNames([locale], { type: 'language' }).of(code);
    // DisplayNames echoes the input back when it has no translation for it.
    return name && name !== code ? name : null;
  } catch {
    return null;
  }
}

export const LOCALE_OPTIONS: LocaleOption[] = SUPPORTED_LOCALES.map((l) => {
  const english = displayNameIn('en', l.code) ?? l.nativeLabel;
  return {
    code: l.code,
    native: l.nativeLabel,
    english,
    searchText: `${l.nativeLabel} ${english} ${l.code}`.toLowerCase(),
  };
});

/** Case-insensitive match across native name, English name, and locale code. */
export function filterLocaleOptions(
  query: string,
  options: LocaleOption[] = LOCALE_OPTIONS,
): LocaleOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) => o.searchText.includes(q));
}

export function localeOption(code: string): LocaleOption | undefined {
  return LOCALE_OPTIONS.find((o) => o.code === code);
}
